import { GetProductDataModel } from "features/stock-ordering/core/domain/get-product-data.model";
import { memo, ReactElement, useEffect, useRef, useState } from "react";
import { concat } from "lodash";
import { ActiveProcessModel } from "features/stock-ordering/core/domain/actove-process.model";
import { useAppSelector, useQuery } from "features/config/hooks";
import { selectStockActiveProcess } from "../slices/stock-active-process.slice";
import { AlertCircle, LucideIcon } from "lucide-react";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { AdminSessionModel } from "features/admin/core/domain/admin-session.model";

/**
 * @deprecated This must be database driven. So im tagging it as deprecated.
 * @description constant for category type for multim products.
 */
export const categoryType = [
  { id: 1, name: "frozen" },
  { id: 2, name: "dry" },
];

/**
 * @deprecated -I dont recall if this currently being used. I refactored the upload files for stock module.
 * I might remove this in the future if found not being used.
 * @description - Checks if the uploaded file is valid.
 * -must be jpg, jpeg, zip
 * @param file - file
 * @param isStore - check if store
 * @returns boolean
 */
export const isValidFile = (
  file: string | File | undefined,
  isStore: boolean
): boolean => {
  if (!file) {
    return false;
  }

  if (typeof file === "string") {
    return true;
  }

  const allowedExtensions = isStore ? ["jpg", "jpeg"] : ["zip"];

  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  const isValidExtension =
    fileExtension && allowedExtensions.includes(fileExtension);

  if (!isValidExtension) {
    return false;
  }

  const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSizeInBytes) {
    return false;
  }

  return true;
};

/**
 * @description - iterates each product data date to check if commtied or delivered quantity is empty
 * @param productData - Ordered product data
 * @param process - current process number
 * @returns boolean
 */
export const isQuantityEmpty = (
  productData: GetProductDataModel["product_data"],
  process: number | undefined
) => {
  if (process === 4) {
    return productData.some(
      (product) => product.dispatch_qty === null || product.dispatch_qty === 0
    );
  } else if (process === 5) {
    return productData.some((product) => {
      if (
        product.delivered_qty === null ||
        product.delivered_qty === undefined
      ) {
        return true;
      }

      if (product.dispatch_qty === null) {
        return product.delivered_qty !== product.commited_qty;
      } else {
        return product.delivered_qty !== product.dispatch_qty;
      }
    });
  } else if (process === 6) {
    return productData.some((product) => {
      if (product.dispatch_qty === null) {
        return product.delivered_qty !== product.commited_qty;
      } else {
        return product.delivered_qty !== product.dispatch_qty;
      }
    });
  }

  return false;
};

/**
 * @description - Arrow function that returns formatted date or formatted datetime
 * @param date - Date as a string.
 * @param withTime - Determines if returned formatted dates comes with time
 * @returns - formatted date or formatted date and time if with time is enabled
 */
export const dateSetup = (
  date: string,
  withTime: boolean,
  dayOnly?: boolean
) => {
  if (date === "" || date === null) {
    return null;
  }

  if (dayOnly) {
    return new Date(date).toLocaleDateString("en-PH", {
      weekday: "short",
    });
  } else {
    if (withTime) {
      return new Date(date).toLocaleDateString("en-PH", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
      });
    } else {
      return new Date(date).toLocaleDateString("en-PH", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  }
};

/**
 * @description - Eliminated the tabs that are not needed for the module
 * - permission 6 and 7 are currently part of the sales modules because it was moved. And is not being used.
 * @param payable - boolean
 * @param index - (number) index of tab or process index
 * @returns boolean if index matches the id.
 */
export const eliminateTab = (payable: boolean, index: number) => {
  if (!payable) {
    // return index === 6 || index === 7 || index === 8;
    return index === 6 || index === 7;
  } else {
    return (
      index === 0 ||
      index === 1 ||
      index === 2 ||
      index === 3 ||
      index === 4 ||
      index === 5
    );
  }
};

/**
 * @description - Separator for tab wherein if the current modules is sales it will eliminate the
 * update billing, pay billing, and confirm payment
 * - Permission id (7)update billing, (8)pay billing, & 9(confirm payment) -
 * is moved from stock module process to sales module paybill.
 * currently part of sales modules and because of new system, the feature is being used.
 *
 * @param payable - boolean
 * @param permission - user permission for processes
 * @returns - permission ids of enabled the enabled processes in stock module
 */
export const isPayableCheck = (
  payable: boolean,
  permission:
    | AdminSessionModel["admin"]["user_details"]["sos_groups"]
    | AdminSessionModel["admin"]["user_details"]["sos_groups_seller"]
) => {
  if (payable) {
    return permission.find(
      (perm) => perm.id === 7 || perm.id === 8 || perm.id === 9
    );
  } else {
    return permission.find(
      (perm) =>
        perm.id === 1 ||
        perm.id === 2 ||
        perm.id === 3 ||
        perm.id === 4 ||
        perm.id === 5 ||
        perm.id === 6 ||
        perm.id === 10
    );
  }
};

/**
 * Determines and returns the type of the file
 *
 * @param file - the file
 * @returns file type
 */
export const getFileType = (file: File | string) => {
  if (typeof file === "string") {
    return "Unknown file type";
  }
  return file.type;
};

/**
 * Determine session data user permission property either ordering or selling
 *
 * @param view - determine if selling or ordering based on url parameter
 * @returns - If view is order returns sos_groups. If selling returns sos_groups_seller
 */
export const groupPropertySelector = (view: string | undefined) => {
  if (view === "order") {
    return "sos_groups";
  }
  if (view === "selling") {
    return "sos_groups_seller";
  }
};

/**
 * Tab indicator styles for process tab, in which where the user has action
 *
 * @param user_details - session data that contains user session information
 * @param index - tab index to point where the changes will occur
 * @param view - determine if selling or ordering based on url parameter
 * @returns - color of the of the tab indicator based on user permission and view
 */
export const tabIndicatorSetter = (
  user_details: AdminSessionModel["admin"]["user_details"] | undefined, //user details
  tabValue: number, //tab index
  view: string | undefined //selling or order
) => {
  const property = groupPropertySelector(view);

  if (property) {
    const multiMCustomPerm =
      user_details?.[property].find((grp) => grp.id === 4) &&
      tabValue === 0 &&
      user_details.stock_seller_company_id === 1;

    const basePermSettings = user_details?.[property].find(
      (group) => tabValue + 1 === group.id && group.id !== 10 && group.id !== 11
    );

    return multiMCustomPerm || basePermSettings ? "white" : "#a21013";
  }
};

/**
 * Styles for process tab, in which where the user has action
 *
 * @param actionProcess - action access data
 * @param index - id of the status id
 * @returns - style on tab indicator based on user permission and view
 */
export const tabStyleSettings = (
  actionProcess: ActiveProcessModel["process_action_access"] | undefined,
  index: number
) => {
  return actionProcess?.map((perm) =>
    perm.status_id === index
      ? {
          backgroundColor: "#a21013",
          // borderTopRightRadius: 5,
          // borderTopLeftRadius: 5,
          paddingY: 1,
          gap: 2,
        }
      : { Color: "white", paddingY: 1 }
  );
};

/**
 * Identifies order supplier based on user permission
 *
 * @param view - determine which type if selling or ordering
 * @param perm - session data that contains user permission
 */
export const fetchOrderSupplierIdentifier = (
  view: string | undefined,
  perm: AdminSessionModel | undefined
) => {
  switch (view) {
    case "selling":
      return perm?.admin.user_details.stock_seller_company_id;
    case "order":
      return perm?.admin.user_details.stockOrderSuppliers;
  }
};

/**
 * Cost Computations
 * @param cost - cost of the product
 * @param quantity - quantity of the product
 */

export const totalCompute = (cost: number, quantity: number) => {
  return (cost * quantity).toFixed(2);
};

/**
 * This hides the elements if the data is null or undefined
 * @param def (default) - data to be displayed
 * @param alt (optional) - alternative data if not exist
 * @param label (optional) - If the label exist
 * @param defClassName (optional) - custom css for the data
 * @param labClassName (optional) - custom css for the label
 */
export const DisplayData = (dispDataProps: {
  def: any;
  alt?: any;
  label?: string;
  defClassName?: string | null;
  labClassName?: string;
}) => {
  //Destructure props
  const { def, alt, label, defClassName, labClassName } = dispDataProps;

  /**
   * @returns alt - if def is empty, null, undefined
   * @return def - if the data is present
   */
  if (def === undefined || def === null) {
    if (alt) {
      return (
        <>
          {label && <span className={`${labClassName ?? ""}`}>{label}</span>}
          <span className={`${defClassName ?? ""}`}>{alt}</span>
        </>
      );
    } else {
      return null;
    }
  } else {
    return (
      <>
        {label && <span className={`${labClassName ?? ""}`}>{label}</span>}
        <span className={`${defClassName ?? ""}`}>{def}</span>
      </>
    );
  }
};

/**
 * Custom component for timeline logs in stock
 * @param style - css for whole block
 * @param steps - stepper data
 * @returns Vertical Stepper Component
 */
export function CustomStepper(customStepperProps: {
  style: string;
  steps: GetProductDataModel["order_information"]["tracking"];
}) {
  const { style, steps } = customStepperProps;

  const activeStep = steps.length;
  const stepperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    stepperRef.current?.scrollTo({
      top: stepperRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [steps]);

  return (
    <div
      ref={stepperRef}
      onMouseDown={(e) => draggableMouseAction(e, stepperRef.current)}
      // style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      className={`${style} flex flex-col space-y-4 px-1`}
    >
      {steps.map((step, index) => (
        <div key={index} className="flex items-start space-x-4">
          {/* Step Indicator */}
          <div className="relative">
            <div
              className={`w-6 h-6 flex items-center justify-center rounded-full text-white font-bold ${
                index === activeStep
                  ? "bg-primary"
                  : index < activeStep
                  ? "bg-button"
                  : "bg-gray-300"
              }`}
            >
              {index + 1}
            </div>
            {index !== steps.length - 1 && (
              <div
                className="w-1 absolute left-[50%] translate-x-[-50%] bg-button"
                style={{ top: "100%", height: "calc(100% + 35px)" }}
              ></div>
            )}
          </div>

          {/* Step Details */}
          <div className="grid grid-cols-2 w-full">
            <div>
              <p className="font-semibold">{step.title}</p>
              <p className="capitalize">
                {concat(step.first_name, " ", step.last_name)}
              </p>
            </div>
            <div className="flex flex-col text-gray-500">
              <span className="self-end">
                {dateSetup(step.datetime, false)}
              </span>
              <span className="self-end">
                {dateSetup(step.datetime, false, true)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * handles draggable scroll
 * @param e - react mouse event
 * @param container - stepperRef
 */
export const draggableMouseAction = (
  event: React.MouseEvent,
  container: HTMLDivElement | null
) => {
  if (!container) return;

  const startX = event.clientX;
  const startY = event.clientY;
  const scrollLeft = container.scrollLeft;
  const scrollTop = container.scrollTop;

  const onMouseMove = (e: MouseEvent) => {
    const distanceX = startX - e.clientX;
    const distanceY = startY - e.clientY;

    container.scrollLeft = scrollLeft + distanceX;
    container.scrollTop = scrollTop + distanceY;
  };

  const onMouseUp = () => {
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };

  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
};

/**
 * format date as hours ago, days ago, weeks ago, years ago
 * @param timestamp - date to format
 * @returns formatted date as ago,
 * example date and time: 2024-10-09 13:42:07
 */
export const formatTimeAgo = (timestamp: string): string => {
  const date = new Date(timestamp + " UTC"); // Ensure it's treated as UTC

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600)
    return rtf.format(-Math.floor(diffInSeconds / 60), "minutes");
  if (diffInSeconds < 86400)
    return rtf.format(-Math.floor(diffInSeconds / 3600), "hours");

  const diffInDays = Math.floor(diffInSeconds / 86400);
  if (diffInDays === 1) return "yesterday";
  if (diffInDays < 7) return rtf.format(-diffInDays, "days");
  if (diffInDays < 30) return rtf.format(-Math.floor(diffInDays / 7), "weeks");
  if (diffInDays < 365)
    return rtf.format(-Math.floor(diffInDays / 30), "months");

  return rtf.format(-Math.floor(diffInDays / 365), "years");
};

/**
 * @param props.title -  optional: Title of the badge
 * @param props.titleIcon - optional: title icon
 * @param props.content - content text
 * @param props.bgColor - optional: background color
 * @param props.textColor - optional: text color
 * @param props.textColor - optional: title text color
 * @returns badge indictor component
 */
export const BannerIndicator = (props: {
  visible?: boolean;
  title?: string;
  titleIcon?: ReactElement;
  content: string;
  bgColor?: string;
  textColor?: string;
  titleColor?: string;
}) => {
  const { visible, title, titleIcon, content, bgColor, textColor } = props;

  if (visible) {
    return (
      <div
        className={`flex p-5 ${bgColor} space-x-2 w-full font-[Roboto] border border-gray-200 shadow-sm rounded-sm`}
      >
        <div className="flex space-x-1">
          {titleIcon && <span className="self-center">{titleIcon}</span>}
          {title && (
            <span
              className={`font-bold ${textColor} tracking-wide whitespace-nowrap self-center`}
            >
              {title}
            </span>
          )}
        </div>
        <p className={`${textColor} font-medium self-center`}>{content}</p>
      </div>
    );
  }

  return null;
};

/**
 * @param visible: boolean | optional - conponent visiblity
 * @param visibleConfirm: boolean | optional - confirm visibility
 * @param visibleCancel: boolean | optional - cancel visibility
 * @param disableConfirm: boolean | optional - disable confirm button
 * @param handleCancelledOrder: () => void | optional - action for cancel
 * @param handleConfirm: () => void | optional - action for confirm
 * @param confirmButtonName: string | optional - custom name for confirm
 * @param cancelButtonName: string | optional - custom name for cancel
 * @returns cancel and confirm button
 */
export const CancelAndConfirm = (props: {
  visible?: boolean;
  disableConfirm?: boolean;
  handleCancelledOrder?: () => void;
  handleConfirm?: () => void;
  confirmButtonName?: string;
  cancelButtonName?: string;
  customButtons?: {
    label: string;
    onClick: () => void;
    position?: "start" | "middle" | "end";
    disabled?: boolean;
  }[];
}) => {
  const {
    visible = true,
    disableConfirm = false,
    handleCancelledOrder,
    handleConfirm,
    confirmButtonName = "Confirm order",
    cancelButtonName = "Cancel order",
    customButtons = [],
  } = props;

  if (!visible) return null;

  // Reusable confirm-style button class
  const confirmStyle = (disabled?: boolean) =>
    `rounded-xl py-3 px-6 font-medium transition-all duration-200 transform shadow-lg ${
      disabled
        ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60 shadow-none"
        : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:scale-105 hover:shadow-xl"
    }`;

  const cancelStyle =
    "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 py-3 px-6 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md border border-gray-200";

  // Helper to render custom buttons by position
  const renderCustomButtons = (pos: "start" | "middle" | "end") =>
    customButtons
      .filter((btn) => btn.position === pos)
      .map((btn, i) => (
        <button
          key={`${pos}-${i}`}
          onClick={btn.onClick}
          disabled={btn.disabled}
          className={confirmStyle(btn.disabled)}
        >
          {btn.label}
        </button>
      ));

  return (
    <div className="flex space-x-4 justify-end text-sm capitalize">
      {/* Start custom buttons */}
      {renderCustomButtons("start")}

      {/* Cancel */}
      {handleCancelledOrder && (
        <button onClick={handleCancelledOrder} className={cancelStyle}>
          {cancelButtonName}
        </button>
      )}

      {/* Middle custom buttons */}
      {renderCustomButtons("middle")}

      {/* Confirm */}
      {handleConfirm && (
        <button
          disabled={disableConfirm}
          onClick={handleConfirm}
          className={confirmStyle(disableConfirm)}
        >
          {confirmButtonName}
        </button>
      )}

      {/* End custom buttons */}
      {renderCustomButtons("end")}
    </div>
  );
};

/**
 * hooks that checks if the user action access
 * @param permId     - user permission
 *
 * @return permission - returns if current user has enrolled permission
 * @return actionAvailable - returns  boolean if it has action on current process
 *
 */
export const useActionAvailability = (permId?: number) => {
  const query = useQuery();
  const currentTab = query.get("tab");

  const stockActiveProcessState = useAppSelector(selectStockActiveProcess);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const { company } = getAdminSessionState.data?.admin.user_details ?? {};

  const { process, process_action_access } = stockActiveProcessState.data ?? {};

  const perm =
    !!permId &&
    Array.isArray(process_action_access) &&
    process_action_access.some((item) => item.permission_id === permId);

  const processId = process?.find((proc) => {
    return proc.seqId - 1 === Number(currentTab) + 1;
  })?.id;

  return {
    permission: perm,
    processId: processId,
    actionAvailable: process_action_access?.some(
      (perm) => perm.status_id === processId
    ),
  };
};

export function useDelayedEmptyState(
  isEmpty: boolean,
  delaySeconds: number = 30
) {
  const [showEmpty, setShowEmpty] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(delaySeconds);

  useEffect(() => {
    if (isEmpty) {
      setShowEmpty(false);
      setTimeRemaining(delaySeconds);
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setShowEmpty(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isEmpty, delaySeconds]);

  return { showEmpty, timeRemaining, isWaiting: !isEmpty && !showEmpty };
}

/**
 * Inner content of the default notice (delivery lead times).
 * Used both by the standalone NoticeBanner and inside DynamicNoticeBanner
 * when the user selects the default notice from the dropdown.
 */
export function NoticeBannerContent() {
  return (
    <>
      <span className="block text-yellow-700 text-xs mt-1 italic">
        Notice released on: September 10, 2025
      </span>

      <p className="text-yellow-800 text-sm mt-2 leading-relaxed">
        To improve scheduling accuracy and avoid delivery conflicts, we have
        updated our lead time requirements.{" "}
        <strong>
          Lead time refers to the minimum number of calendar days required
          between the date of order placement and the earliest eligible
          delivery date.
        </strong>{" "}
        This change is necessary because orders placed on days preceding the
        weekend may not be reviewed or approved by procurement until the
        following Monday, so deliveries must account for this delay.
      </p>

      <div className="mt-4">
        <table className="w-full text-sm text-yellow-900 border-collapse">
          <thead>
            <tr className="text-left border-b border-yellow-300">
              <th className="py-1 px-2 font-semibold">Order Placed On</th>
              <th className="py-1 px-2 font-semibold">Minimum Lead Time</th>
              <th className="py-1 px-2 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-yellow-200">
            <tr>
              <td className="py-1 px-2 font-medium">Friday</td>
              <td className="py-1 px-2">
                <span className="bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-semibold">
                  5 days
                </span>
              </td>
              <td className="py-1 px-2 text-xs">
                First available delivery will fall on the following Wednesday.
              </td>
            </tr>
            <tr>
              <td className="py-1 px-2 font-medium">Saturday</td>
              <td className="py-1 px-2">
                <span className="bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-semibold">
                  4 days
                </span>
              </td>
              <td className="py-1 px-2 text-xs">
                Orders placed on Saturday cannot be delivered earlier than
                Wednesday.
              </td>
            </tr>
            <tr>
              <td className="py-1 px-2 font-medium">Sunday</td>
              <td className="py-1 px-2">
                <span className="bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-semibold">
                  4 days
                </span>
              </td>
              <td className="py-1 px-2 text-xs">
                Orders can be placed on Sunday, but deliveries will not occur
                on Sunday.
              </td>
            </tr>
            <tr>
              <td className="py-1 px-2 font-medium">Standard (Mon-Thu)</td>
              <td className="py-1 px-2">
                <span className="bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-semibold">
                  3 days
                </span>
              </td>
              <td className="py-1 px-2 text-xs">
                Applies to weekday orders excluding Friday.
              </td>
            </tr>
            <tr>
              <td className="py-1 px-2 font-medium">No Sunday Deliveries</td>
              <td className="py-1 px-2">—</td>
              <td className="py-1 px-2 text-xs">
                Deliveries will not be scheduled on Sundays under any
                circumstance.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-yellow-700 text-xs mt-3 italic">
        ⚠️ These rules are effective immediately and apply to all future
        orders. Please plan accordingly to avoid scheduling issues.
      </p>

      <p className="text-yellow-800 text-sm mt-2">
        For any questions or clarifications regarding these updated lead
        times, please contact: <br />
        <strong>Allan Solis</strong> - MIS Manager <br />
        {/* Email: <span className="underline">
          ken.oresca.tei@gmail.com
        </span>{" "}
        <br /> */}
        Phone: <span className="underline">+63 949 889 9553</span>
      </p>
    </>
  );
}

export const NoticeBanner = memo(() => {
  return (
    <div
      role="alert"
      className="relative flex p-5 bg-yellow-50 w-full font-[Roboto] border border-yellow-300 shadow-md rounded-lg"
    >
      <span className="self-start text-yellow-700 mr-3">
        <AlertCircle size={26} />
      </span>

      <div className="flex flex-col flex-1">
        <span className="block font-bold text-lg text-yellow-900">
          Important Notice: Updated Delivery Lead Times
        </span>

        <NoticeBannerContent />
      </div>
    </div>
  );
});
/**
 * Adds thousand separator for displaying values
 * @param {required} value number or string to format
 * @param {optional} precision - number of decimal points
 *
 * @returns formatted string with separators w/o precision
 */
export const moneyFormatter = (
  value: number | string | undefined,
  precision?: number
): string => {
  const num = typeof value === "number" ? value : Number(value);

  if (isNaN(num)) {
    return precision ? Number("0").toFixed(precision) : "0";
  }

  if (precision !== undefined) {
    return num.toLocaleString("en-PH", {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  }

  // Default: no fixed precision
  return num.toLocaleString("en-PH");
};

export const ActionHeader = ({
  primary,
  secondary,
  child,
  HeaderIcon,
}: {
  primary: string;
  secondary?: string;
  child?: ReactElement;
  HeaderIcon: React.ElementType;
}) => {
  return (
    <div className="flex items-center mb-4">
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
        <HeaderIcon className="w-6 h-6 text-blue-600" />
      </div>
      <div className="ml-3">
        <h2 className="font-semibold text-gray-800">{primary}</h2>

        {secondary && <p className="text-sm text-gray-500">{secondary}</p>}
        {child && child}
      </div>
    </div>
  );
};
